import { createAction, createAsyncThunk } from "@reduxjs/toolkit";
import { AuthRequest, CoreRequest } from "../config/serviceInstances";
import { AuthEndpoints } from "../config/endpointsAuth";
import qs from "query-string";
import { CoreEndpoints } from "../config/endpointsCore";
import {
  ApiCallState,
  ApiCallStateInitial,
  GenericError,
  LoginBySessionInitial,
} from "../api-model/api";
import { RoleResponse, UpdateUser, UserDto } from "../api-model/general/auth";
import { AxiosResponse } from "axios";
import { ActionType, handleActionWithFeedback } from "../store/errors";

export type AccountRoles = {
  COMPANY_ACCESS: RoleResponse[];
  DEPARTMENT_ACCESS: RoleResponse[];
  WORK_CONTRACT_ACCESS: RoleResponse[];
  ALL_ROLES: RoleResponse[];
};

export interface Roles {
  COMPANY_ACCESS: string[];
  DEPARTMENT_ACCESS: string[];
  WORK_CONTRACT_ACCESS: string[];
  ALL_ROLES: string[];
}

export const defaultRoles: Roles = {
  COMPANY_ACCESS: [],
  DEPARTMENT_ACCESS: [],
  WORK_CONTRACT_ACCESS: [],
  ALL_ROLES: [],
};

const defaultAccountRoles: AccountRoles = {
  COMPANY_ACCESS: [],
  DEPARTMENT_ACCESS: [],
  WORK_CONTRACT_ACCESS: [],
  ALL_ROLES: [],
};

export interface LoginBySessionInterface extends ApiCallState {
  data: Record<string, unknown>;
  fulfilled: boolean;
}

export interface AuthorizationState {
  authentication: boolean;
  session: boolean;
  roles: Roles;
  accountRoles: AccountRoles;
  refreshToken: string;
  refreshInstance: number;
  loginByCredentials: ApiCallState;
  refreshTokenRequest: ApiCallState;
  loginBySession: LoginBySessionInterface;
  logOut: ApiCallState;
}

export const authorizationStateDefault: AuthorizationState = {
  authentication: false,
  session: false,
  roles: defaultRoles,
  accountRoles: defaultAccountRoles,
  refreshToken: "",
  refreshInstance: 0,
  loginByCredentials: ApiCallStateInitial,
  refreshTokenRequest: ApiCallStateInitial,
  loginBySession: LoginBySessionInitial,
  logOut: ApiCallStateInitial,
};

type SomeAuthResponse = AuthorizationResponse | AuthorizationBySessionResponse;

export interface AuthorizationResponse {
  access_token: string;
  token_type: string;
  refresh_token: string;
  expires_in: number;
  scope: string;
  jti: string;
}

export interface AuthorizationBySessionResponse {
  accessToken: string;
  detail: string;
  refreshToken: string;
  session: boolean;
  timeStamp: Date;
}

export enum SessionStatus {
  PENDING,
  LOGGED_IN,
  LOGGED_OUT,
  AWAITING_ROLE_SELECTION,
}

export type SetAuthData = {
  session: SomeAuthResponse | null;
  account: UserDto | null;
  sessionStatus: SessionStatus;
  error?: string | null;
};

export type LoginCredentials = { username: string; password: string };

const authConfig = {
  withCredentials: true,

  headers: {
    "Content-Type": "application/x-www-form-urlencoded",
  },
  params: {
    grant_type: "password",
    scope: "any",
  },
  auth: {
    username: "alex",
    password: "alex",
  },
};

export const refreshConfig = {
  ...authConfig,
  headers: {
    "Content-Type": "application/x-www-form-urlencoded",
  },
  params: { grant_type: "refresh_token", scope: "any" },
};

export const config = {
  auth: {
    username: "alex",
    password: "alex",
  },
  withCredentials: true,
  headers: {
    "Content-Type": "application/x-www-form-urlencoded",
  },
};

export const refreshToken = createAsyncThunk<AuthorizationResponse, string>(
  "/authentication/refresh",
  async (rToken) => {
    const response = await AuthRequest.post(
      AuthEndpoints.GET_TOKEN,
      qs.stringify({
        refresh_token: rToken,
      }),
      refreshConfig
    );

    if (response.status > 299) {
      // Return the known error for future handling
      return response.data.error;
    }
    return response.data as AuthorizationResponse;
  }
);

export const fetchSelf = createAsyncThunk<UserDto>(
  "/authentication/self",
  async () => {
    const response = await CoreRequest.get(CoreEndpoints.EMPLOYEE_SELF);

    if (response.status > 299) {
      // Return the known error for future handling
      return response.data.error;
    }
    return response.data as UserDto;
  }
);

export const updateSelf = createAsyncThunk<UserDto, UpdateUser>(
  "/account/update",
  async (d, thunkAPI) => {
    return handleActionWithFeedback(
      () => {
        return CoreRequest.put("/account/update", d);
      },
      thunkAPI,
      ActionType.update,
      "resource.account"
    );
  }
);

// Authentication check and flow
// 1. Check if HEAVEN_SESSION cookie is available (has to be done server side)
// 2. Pull account details
// 3. Decode token
// 4. Store all data in redux
// 1.1. Or mark used as not logged in
export const authenticateByCookie = createAsyncThunk<SetAuthData>(
  "/authentication/firstLoad",
  async () => {
    try {
      // Always returns 200, need to check 'session' property for true/false
      const response: AxiosResponse<AuthorizationBySessionResponse> =
        await AuthRequest.get(AuthEndpoints.CHECK_SESSION, config);

      if (!response.data.session) {
        // Session not valid anymore, or never was
        return {
          session: null,
          account: null,
          sessionStatus: SessionStatus.LOGGED_OUT,
        };
      } else {
        // Extract the tokens and details
        const token = response.data.accessToken;
        // Get the account details (with role details), returns 401 with invalid session
        const accountResponse: AxiosResponse<UserDto> = await CoreRequest.get(
          CoreEndpoints.EMPLOYEE_SELF,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        return {
          session: response.data,
          account: accountResponse.data,
          sessionStatus: SessionStatus.LOGGED_IN,
        };
      }
    } catch (error) {
      return {
        session: null,
        account: null,
        sessionStatus: SessionStatus.LOGGED_OUT,
      };
    }
  }
);

export const authenticateFullyByCredentials = createAsyncThunk<
  SetAuthData,
  LoginCredentials
>("/authentication/login/fully", async (credentials: LoginCredentials) => {
  try {
    const response: AxiosResponse<AuthorizationResponse> =
      await AuthRequest.post(
        AuthEndpoints.GET_TOKEN,
        qs.stringify(credentials),
        authConfig
      );

    // Extract the tokens and details
    const token = response.data.access_token;
    // Get the account details (with role details), returns 401 with invalid session
    const accountResponse: AxiosResponse<UserDto> = await CoreRequest.get(
      CoreEndpoints.EMPLOYEE_SELF,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    return {
      session: response.data,
      account: accountResponse.data,
      sessionStatus: SessionStatus.LOGGED_IN,
    };
  } catch (error: unknown) {
    // @ts-ignore
    const e: GenericError = error.response.data;
    return {
      session: null,
      account: null,
      sessionStatus: SessionStatus.LOGGED_OUT,
      error: e.error,
    };
  }
});

export const enterDemoAccount = createAction("/demo");

export const logOut = createAsyncThunk("/authentication/logout", async () => {
  const response = await AuthRequest.post(AuthEndpoints.LOGOUT, config, {
    withCredentials: true,
  });

  window.location.href = "/app/login";

  return response.data as string;
});

export const setAuthData = createAction<SetAuthData>("/authentication/setData");
